import { useState, useEffect, useRef, useCallback, forwardRef, useImperativeHandle } from 'react';
import {
  Box, Typography, TextField, Button, CircularProgress, Alert, Chip,
  Table, TableBody, TableCell, TableHead, TableRow, Collapse,
  useMediaQuery, useTheme,
} from '@mui/material';
import { ThemeProvider } from '@mui/material/styles';
import MicNoneIcon from '@mui/icons-material/MicNone';
import PauseRoundedIcon from '@mui/icons-material/PauseRounded';
import PlayArrowRoundedIcon from '@mui/icons-material/PlayArrowRounded';
import StopRoundedIcon from '@mui/icons-material/StopRounded';
import PersonAddAltOutlinedIcon from '@mui/icons-material/PersonAddAltOutlined';
import { QRCodeSVG } from 'qrcode.react';
import {
  API_BASE_URL, TUTOR_API_BASE_URL, ROUTE, WS_BASE_URL, CLIENT_ID,
  TUTOR_DEVICE_ID, SEGMENT_LENGTH_FALLBACK, HEARTBEAT_MS, DEVICE_STATE, ROLE,
} from './StreamConfig';
import { buildQrTarget } from './QrTarget';
import StreamHelpDialog from '../popups/StreamHelpDialog';
import { getAuthToken, getLoggedInUser } from '../../utils/auth';
import { COLORS, FONT_FAMILY, appTheme, BUTTON_HOVER_SX } from '../../talkTreeShared/Tree/constants/theme';

const BLUE = COLORS.primary;
const RED = '#dc2626';
const BAR_COUNT = 7;
const MIN_SEGMENT_BYTES = 2048;

const STEP = { SETUP: 'setup', CONTROL: 'control', DONE: 'done' };

const REC = {
  IDLE: 'idle', REQUESTING: 'requesting', DENIED: 'denied',
  RECORDING: 'recording', PAUSED: 'paused',
};

const TUTOR_SESSION_KEY = 'tutor_session';

const formatMicError = (err) => {
  switch (err && err.name) {
    case 'NotAllowedError':
    case 'SecurityError':
      return 'Microphone access was blocked. Allow it in your browser settings, then try again.';
    case 'NotFoundError':
    case 'DevicesNotFoundError':
      return 'No microphone was found. Connect one and try again.';
    case 'NotReadableError':
      return 'Your microphone is in use by another app. Close it and try again.';
    default:
      return 'Could not access the microphone. Check your settings and try again.';
  }
};

const pickMimeType = () => {
  if (typeof MediaRecorder === 'undefined' || !MediaRecorder.isTypeSupported) return '';
  return ['audio/webm;codecs=opus', 'audio/webm', 'audio/mp4', 'audio/ogg']
    .find((t) => MediaRecorder.isTypeSupported(t)) || '';
};

const stateChipSx = (state) => {
  const map = {
    [DEVICE_STATE.RECORDING]: { bg: '#fef2f2', fg: RED },
    [DEVICE_STATE.PAUSED]: { bg: '#fffbeb', fg: '#b45309' },
    [DEVICE_STATE.STOPPED]: { bg: '#f3f4f6', fg: COLORS.text },
    [DEVICE_STATE.STALE]: { bg: '#f3f4f6', fg: '#9ca3af' },
    [DEVICE_STATE.INITIATED]: { bg: '#eff4ff', fg: BLUE },
    [DEVICE_STATE.WAITING]: { bg: '#eff4ff', fg: BLUE },
  };
  const c = map[state] || { bg: '#f3f4f6', fg: COLORS.text };
  return { backgroundColor: c.bg, color: c.fg, fontWeight: 600, fontSize: '12px', height: 24, borderRadius: '999px' };
};

const StreamSetup = forwardRef(({ isTokenExpired, onGoToRecordings, showSuccessToast, onSessionActiveChange }, ref) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const userId = getLoggedInUser() || '';

  const getEmailFromToken = () => {
    try {
      const t = getAuthToken();
      if (!t) return '';
      const parts = t.split('.');
      if (parts.length !== 3) return '';
      return JSON.parse(atob(parts[1])).email || '';
    } catch {
      return '';
    }
  };
  const tutorId = getEmailFromToken() || userId;

  const [step, setStep] = useState(STEP.SETUP);
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [error, setError] = useState('');
  const [isInitiating, setIsInitiating] = useState(false);

  const [sessionId, setSessionId] = useState('');
  const [qrUrl, setQrUrl] = useState('');
  const [joinCode, setJoinCode] = useState('');
  const [showQr, setShowQr] = useState(false);

  const [recPhase, setRecPhase] = useState(REC.IDLE);
  const [recError, setRecError] = useState('');
  const [elapsed, setElapsed] = useState(0);
  const [bars, setBars] = useState(() => Array(BAR_COUNT).fill(0.08));

  const [roster, setRoster] = useState([]);
  const [stopping, setStopping] = useState(false);
  const [wsConnected, setWsConnected] = useState(true);

  const [helpOpen, setHelpOpen] = useState(false);
  const [helpSection, setHelpSection] = useState('main');
  const openHelp = useCallback((section = 'main') => {
    setHelpSection(section);
    setHelpOpen(true);
  }, []);

  const sessionIdRef = useRef('');
  const sessionTitleRef = useRef('');
  const credentialRef = useRef(null);
  const segmentLenRef = useRef(SEGMENT_LENGTH_FALLBACK);
  const serverAnchorMsRef = useRef(0);
  const joinPerfRef = useRef(0);
  const segmentIndexRef = useRef(0);
  const segStartMsRef = useRef(0);
  const uploadsRef = useRef([]);
  const isStoppingRef = useRef(false);
  const segmentTimerRef = useRef(null);
  const syncSentPerfRef = useRef(0);

  const streamRef = useRef(null);
  const recorderRef = useRef(null);
  const audioCtxRef = useRef(null);
  const rafRef = useRef(null);
  const lastVisualRef = useRef(0);
  const timerRef = useRef(null);
  const elapsedRef = useRef(0);

  const wsRef = useRef(null);
  const heartbeatRef = useRef(null);
  const reconnectRef = useRef(null);
  const closedRef = useRef(false);
  const stopTimeoutRef = useRef(null);
  const wsReadyRef = useRef(false);
  const wsQueueRef = useRef([]);
  const startAckTimerRef = useRef(null);
  const startAckResolveRef = useRef(null);
  const startAckRejectRef = useRef(null);

  // Mixpanel tracking refs
  const hasStartedRecordingOnceRef = useRef(false);
  const prevAuxCountRef = useRef(0);
  const sessionStartWallClockRef = useRef(0);
  const rosterRef = useRef([]); // ref so socket-callback closures never read a stale roster

  const nowMs = useCallback(
    () => Math.round(serverAnchorMsRef.current + (performance.now() - joinPerfRef.current)),
    [],
  );

  const stopTimers = useCallback(() => {
    if (timerRef.current) { clearInterval(timerRef.current); timerRef.current = null; }
    if (heartbeatRef.current) { clearInterval(heartbeatRef.current); heartbeatRef.current = null; }
  }, []);

  const cleanupMedia = useCallback(() => {
    if (rafRef.current) { cancelAnimationFrame(rafRef.current); rafRef.current = null; }
    if (audioCtxRef.current && audioCtxRef.current.state !== 'closed') audioCtxRef.current.close().catch(() => { });
    audioCtxRef.current = null;
    if (streamRef.current) { streamRef.current.getTracks().forEach((t) => t.stop()); streamRef.current = null; }
  }, []);

  const cleanupSocket = useCallback(() => {
    closedRef.current = true;
    wsReadyRef.current = false;
    wsQueueRef.current = [];
    if (startAckTimerRef.current) { clearTimeout(startAckTimerRef.current); startAckTimerRef.current = null; }
    startAckResolveRef.current = null;
    startAckRejectRef.current = null;
    if (reconnectRef.current) clearTimeout(reconnectRef.current);
    const ws = wsRef.current;
    if (ws) {
      ws.onmessage = null;
      ws.onerror = null;
      ws.onclose = null;
      if (ws.readyState === WebSocket.CONNECTING) {
        ws.onopen = () => ws.close();
      } else if (ws.readyState === WebSocket.OPEN) {
        ws.close();
      }
    }
    wsRef.current = null;
  }, []);

  useEffect(() => () => { stopTimers(); cleanupMedia(); cleanupSocket(); }, [stopTimers, cleanupMedia, cleanupSocket]);

  const startVisualizer = useCallback((stream) => {
    const Ctx = window.AudioContext || window.webkitAudioContext;
    if (!Ctx) return;
    const ctx = new Ctx();
    const analyser = ctx.createAnalyser();
    analyser.fftSize = 256;
    ctx.createMediaStreamSource(stream).connect(analyser);
    audioCtxRef.current = ctx;
    const data = new Uint8Array(analyser.frequencyBinCount);
    const seg = Math.floor(data.length / BAR_COUNT) || 1;
    const tick = (ts) => {
      rafRef.current = requestAnimationFrame(tick);
      if (ts - lastVisualRef.current < 50) return;
      lastVisualRef.current = ts;
      analyser.getByteFrequencyData(data);
      const next = [];
      for (let i = 0; i < BAR_COUNT; i += 1) {
        let sum = 0;
        for (let j = 0; j < seg; j += 1) sum += data[i * seg + j];
        next.push(Math.max(0.08, sum / seg / 255));
      }
      setBars(next);
    };
    rafRef.current = requestAnimationFrame(tick);
  }, []);

  const freezeVisualizer = useCallback(() => {
    if (rafRef.current) { cancelAnimationFrame(rafRef.current); rafRef.current = null; }
    setBars(Array(BAR_COUNT).fill(0.08));
  }, []);

  const callInitiate = useCallback(async () => {
    if (isTokenExpired) await isTokenExpired();
    const authToken = getAuthToken();
    const url = `${API_BASE_URL}/${ROUTE.INITIATE}`;
    console.log('→ initiate POST', url, '| auth?', !!authToken);
    let res;
    try {
      res = await fetch(url, {
        method: 'POST',
        headers: { Authorization: authToken, 'Content-Type': 'application/json' },
        body: JSON.stringify({
          title: title.trim(),
          description: description.trim(),
          userId,
          tutorId,
          clientId: CLIENT_ID,
        }),
      });
    } catch (e) {
      console.error('✗ initiate fetch threw (network/CORS):', e);
      throw new Error('Could not reach the server. Check your connection and try again.');
    }
    console.log('← initiate status', res.status);
    if (!res.ok) throw new Error(await res.text());
    return res.json();
  }, [isTokenExpired, title, description, userId, tutorId]);

  const callPrepareTutor = useCallback(async () => {
    if (isTokenExpired) await isTokenExpired();
    const authToken = getAuthToken();
    const url = `${TUTOR_API_BASE_URL}/${ROUTE.PREPARE_TUTOR}`;
    console.log('→ prepareTutor POST', url, '| auth?', !!authToken);
    let res;
    try {
      res = await fetch(url, {
        method: 'POST',
        headers: { Authorization: authToken, 'Content-Type': 'application/json' },
        body: JSON.stringify({ token: sessionIdRef.current, role: ROLE.TUTOR }),
      });
    } catch (e) {
      console.error('✗ prepareTutor fetch threw (network/CORS):', e);
      throw new Error('Could not reach the server. Check your connection and try again.');
    }
    console.log('← prepareTutor status', res.status);
    if (res.status === 404) throw new Error('This session no longer exists.');
    if (res.status === 409) throw new Error('This session has already ended.');
    if (!res.ok) throw new Error(`Could not begin streaming (HTTP ${res.status}).`);
    return res.json();
  }, [isTokenExpired]);

  const uploadSegment = useCallback(async (index, startMs, endMs, blob) => {
    const cred = credentialRef.current;
    if (!cred) return;
    const form = new FormData();
    Object.entries(cred.fields).forEach(([k, v]) => form.append(k, v));
    const key = `${CLIENT_ID}/${sessionIdRef.current}/${TUTOR_DEVICE_ID}/seg_${String(index).padStart(3, '0')}_${startMs}_${endMs}`;
    form.set('key', key);
    form.set('x-amz-meta-segment-index', String(index));
    form.set('x-amz-meta-start-ms', String(startMs));
    form.set('x-amz-meta-end-ms', String(endMs));
    form.append('Content-Type', blob.type || 'audio/webm');
    form.append('file', blob);
    let res;
    try {
      res = await fetch(cred.url, { method: 'POST', body: form });
    } catch (netErr) {
      netErr.segmentStatus = 'network_error';
      throw netErr;
    }
    if (!res.ok) {
      const httpErr = new Error(`segment upload failed: ${res.status}`);
      httpErr.segmentStatus = res.status;
      throw httpErr;
    }
  }, []);

  const makeOnDataAvailable = useCallback(() => (e) => {
    if (!e.data || e.data.size < MIN_SEGMENT_BYTES) {
      console.warn('Skipping empty recorder artifact:', e.data?.size || 0, 'bytes');
      return;
    }
    const index = segmentIndexRef.current;
    segmentIndexRef.current += 1;
    try {
      const stored = sessionStorage.getItem(TUTOR_SESSION_KEY);
      if (stored) {
        sessionStorage.setItem(TUTOR_SESSION_KEY, JSON.stringify({ ...JSON.parse(stored), segmentIndex: segmentIndexRef.current }));
      }
    } catch { /* ignore */ }
    const startMs = segStartMsRef.current;
    const endMs = nowMs();
    segStartMsRef.current = endMs;
    const upload = uploadSegment(index, startMs, endMs, e.data)
      .catch((uErr) => {
        console.error(uErr);
        mixpanel.track('segmentUploadFailed', {
          sessionId: sessionIdRef.current,
          segmentIndex: index,
          segmentBytes: e.data.size,
          segmentDurationMs: endMs - startMs,
          status: uErr?.segmentStatus ?? 'unknown',
          errorMessage: uErr?.message || 'Unknown error',
          context: 'own_device',
        });
      })
      .finally(() => {
        uploadsRef.current = uploadsRef.current.filter((pending) => pending !== upload);
      });
    uploadsRef.current.push(upload);
  }, [nowMs, uploadSegment]);

  const sendWs = useCallback((msg) => {
    const ws = wsRef.current;
    if (ws && ws.readyState === WebSocket.OPEN) {
      ws.send(JSON.stringify(msg));
      console.log('→ WS send', msg.type, msg.state || '');
    } else if (!wsReadyRef.current) {
      wsQueueRef.current.push(msg);
      console.warn('⚠ WS queued (not open yet):', msg.type);
    } else {
      console.warn('⚠ WS send DROPPED (socket not open):', msg.type, '| readyState:', ws ? ws.readyState : 'no socket');
    }
  }, []);

  const requestSessionStart = useCallback(() => new Promise((resolve, reject) => {
    let attempts = 0;
    const clear = () => {
      if (startAckTimerRef.current) clearTimeout(startAckTimerRef.current);
      startAckTimerRef.current = null;
      startAckResolveRef.current = null;
      startAckRejectRef.current = null;
    };
    startAckResolveRef.current = () => { clear(); resolve(); };
    startAckRejectRef.current = (err) => { clear(); reject(err); };

    const attempt = () => {
      attempts += 1;
      sendWs({ type: 'SESSION_START' });
      startAckTimerRef.current = setTimeout(() => {
        if (attempts < 5) attempt();
        else startAckRejectRef.current?.(new Error('The server did not confirm session start.'));
      }, 1500);
    };
    attempt();
  }), [sendWs]);

  const handleAllStopped = useCallback((viaTimeout = false) => {
    if (stopTimeoutRef.current) { clearTimeout(stopTimeoutRef.current); stopTimeoutRef.current = null; }
    const durationSeconds = elapsedRef.current;
    mixpanel.track('sessionStopped', {
      sessionId: sessionIdRef.current,
      durationSeconds,
      viaTimeout,
      deviceCountAtStop: rosterRef.current.length || 1,
    });
    stopTimers();
    cleanupMedia();
    cleanupSocket();
    try { sessionStorage.removeItem(TUTOR_SESSION_KEY); } catch { /* ignore */ }
    if (showSuccessToast) {
      showSuccessToast(
        viaTimeout
          ? { type: 'warning', title: 'Recording stopped', message: 'Your session is wrapping up. It may take a little longer than usual to appear below.' }
          : { type: 'success', title: sessionTitleRef.current || 'Recording uploaded', message: 'has been sent for processing.' }
      );
    }
    if (onGoToRecordings) onGoToRecordings();
  }, [stopTimers, cleanupMedia, cleanupSocket, onGoToRecordings, showSuccessToast]);

  const resumeOwnRecordingLocal = useCallback(async (isPaused) => {
    if (recorderRef.current) return;
    if (!navigator.mediaDevices?.getUserMedia) {
      setRecPhase(REC.DENIED);
      setRecError('Recording is not supported in this browser, or the page is not on HTTPS.');
      return;
    }
    setRecPhase(REC.REQUESTING);
    let stream;
    try {
      stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      streamRef.current = stream;
    } catch (err) {
      cleanupMedia();
      setRecPhase(REC.DENIED);
      setRecError(formatMicError(err));
      mixpanel.track('microphonePermissionDenied', { errorName: err?.name || 'unknown', context: 'remote_start' });
      return;
    }
    const mimeType = pickMimeType();
    const recorder = mimeType ? new MediaRecorder(stream, { mimeType }) : new MediaRecorder(stream);
    segStartMsRef.current = nowMs();
    recorder.ondataavailable = makeOnDataAvailable();
    recorder.start();
    segmentTimerRef.current = setInterval(() => {
      if (recorder.state === 'recording') recorder.requestData();
    }, segmentLenRef.current * 1000);
    recorderRef.current = recorder;
    startVisualizer(stream);

    if (!hasStartedRecordingOnceRef.current) {
      hasStartedRecordingOnceRef.current = true;
      mixpanel.track('recordingStarted', { sessionId: sessionIdRef.current, context: 'remote_start' });
    }

    if (isPaused) {
      recorder.requestData();
      recorder.pause();
      if (segmentTimerRef.current) { clearInterval(segmentTimerRef.current); segmentTimerRef.current = null; }
      freezeVisualizer();
      setRecPhase(REC.PAUSED);
    } else {
      elapsedRef.current = 0;
      setElapsed(0);
      timerRef.current = setInterval(() => { elapsedRef.current += 1; setElapsed(elapsedRef.current); }, 1000);
      setRecPhase(REC.RECORDING);
    }
    sendWs({ type: 'STATE_UPDATE', state: isPaused ? 'paused' : 'recording' });
  }, [cleanupMedia, nowMs, makeOnDataAvailable, startVisualizer, freezeVisualizer, sendWs]);

  const openSocket = useCallback((tokenArg) => {
    const tok = tokenArg || sessionId;
    if (!tok) {
      console.warn('openSocket skipped: no sessionId yet (initiate must succeed first)');
      return;
    }
    if (!WS_BASE_URL || WS_BASE_URL.includes('<')) {
      console.error('openSocket: WS_BASE_URL is missing or still a placeholder:', WS_BASE_URL);
      return;
    }
    const qs = new URLSearchParams({ token: tok, deviceId: TUTOR_DEVICE_ID, role: ROLE.TUTOR });
    const wsBase = WS_BASE_URL.replace(/\/+$/, '');
    const wsUrl = `${wsBase}?${qs}`;
    console.log('→ WS connect', wsUrl);
    const ws = new WebSocket(wsUrl);
    wsRef.current = ws;
    ws.onopen = () => {
      wsReadyRef.current = true;
      setWsConnected(true);
      wsQueueRef.current.forEach((msg) => {
        ws.send(JSON.stringify(msg));
        console.log('→ WS flushed', msg.type);
      });
      wsQueueRef.current = [];
      if (heartbeatRef.current) clearInterval(heartbeatRef.current);
      heartbeatRef.current = setInterval(() => sendWs({ type: 'HEARTBEAT' }), HEARTBEAT_MS);
      syncSentPerfRef.current = performance.now();
      sendWs({ type: 'SYNC' });
    };

    ws.onmessage = (evt) => {
      try {
        const msg = JSON.parse(evt.data);
        console.log('← WS recv', msg.type, msg);

        if (typeof msg.serverTime === 'number') {
          const rttMs = performance.now() - syncSentPerfRef.current;
          serverAnchorMsRef.current = msg.serverTime * 1000 + rttMs / 2;
          joinPerfRef.current = performance.now();
        }

        if (msg.type === 'ROSTER') setRoster(msg.devices || []);
        if (msg.type === 'SESSION_START_ACK') startAckResolveRef.current?.();
        if (msg.type === 'SESSION_STATE') { }
        if (msg.type === 'SESSION_START') resumeOwnRecordingLocal(false);
        if (msg.type === 'SESSION_PAUSE') resumeOwnRecordingLocal(true);
        if (msg.type === 'SESSION_ENDED') {
          try { sessionStorage.removeItem(TUTOR_SESSION_KEY); } catch { /* ignore */ }
          handleAllStopped(true);
        }
        if (msg.type === 'ALL_STOPPED') handleAllStopped();
      } catch (e) { /* ignore */ }
    };
    ws.onclose = () => {
      wsReadyRef.current = false;
      setWsConnected(false);
      if (heartbeatRef.current) { clearInterval(heartbeatRef.current); heartbeatRef.current = null; }
      if (!closedRef.current) reconnectRef.current = setTimeout(() => openSocket(tok), 2000);
    };
    ws.onerror = () => ws.close();
  }, [sessionId, handleAllStopped, sendWs, resumeOwnRecordingLocal]);

  const startRecording = useCallback(async () => {
    mixpanel.track('beginStreamingClicked', { sessionId: sessionIdRef.current });
    setRecError('');
    segmentIndexRef.current = 0;

    if (!navigator.mediaDevices?.getUserMedia) {
      setRecPhase(REC.DENIED);
      setRecError('Recording is not supported in this browser, or the page is not on HTTPS.');
      return;
    }

    setRecPhase(REC.REQUESTING);
    let stream;
    try {
      stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      streamRef.current = stream;
    } catch (err) {
      cleanupMedia();
      setRecPhase(REC.DENIED);
      setRecError(formatMicError(err));
      mixpanel.track('microphonePermissionDenied', { errorName: err?.name || 'unknown', context: 'own_device' });
      return;
    }
    try {
      await requestSessionStart();
    } catch (startErr) {
      cleanupMedia();
      setRecPhase(REC.DENIED);
      setRecError(startErr.message || 'Could not start the session. Please try again.');
      return;
    }
    const mimeType = pickMimeType();
    const recorder = mimeType ? new MediaRecorder(stream, { mimeType }) : new MediaRecorder(stream);
    segStartMsRef.current = nowMs();
    recorder.ondataavailable = makeOnDataAvailable();
    recorder.start();
    segmentTimerRef.current = setInterval(() => {
      if (recorder.state === 'recording') recorder.requestData();
    }, segmentLenRef.current * 1000);
    recorderRef.current = recorder;

    startVisualizer(stream);
    elapsedRef.current = 0;
    setElapsed(0);
    timerRef.current = setInterval(() => { elapsedRef.current += 1; setElapsed(elapsedRef.current); }, 1000);
    setRecPhase(REC.RECORDING);

    if (!hasStartedRecordingOnceRef.current) {
      hasStartedRecordingOnceRef.current = true;
      mixpanel.track('recordingStarted', { sessionId: sessionIdRef.current, context: 'own_device' });
    }
  }, [cleanupMedia, requestSessionStart, nowMs, makeOnDataAvailable, startVisualizer]);


  const pauseAll = useCallback(() => {
    mixpanel.track('recordingPaused', { sessionId: sessionIdRef.current, elapsedSeconds: elapsedRef.current, deviceCount: rosterRef.current.length || 1 });
    if (segmentTimerRef.current) { clearInterval(segmentTimerRef.current); segmentTimerRef.current = null; }
    const recorder = recorderRef.current;
    if (recorder?.state === 'recording') {
      recorder.requestData();
      recorder.pause();
    }
    if (timerRef.current) { clearInterval(timerRef.current); timerRef.current = null; }
    freezeVisualizer();
    setRecPhase(REC.PAUSED);
    sendWs({ type: 'STATE_UPDATE', state: 'paused' });
    sendWs({ type: 'SESSION_PAUSE' });
  }, [freezeVisualizer, sendWs]);


  const resumeAll = useCallback(() => {
    mixpanel.track('recordingResumed', { sessionId: sessionIdRef.current, elapsedSeconds: elapsedRef.current, deviceCount: rosterRef.current.length || 1 });
    const recorder = recorderRef.current;
    if (recorder?.state === 'paused') recorder.resume();
    segStartMsRef.current = nowMs();
    if (streamRef.current) startVisualizer(streamRef.current);
    timerRef.current = setInterval(() => { elapsedRef.current += 1; setElapsed(elapsedRef.current); }, 1000);
    segmentTimerRef.current = setInterval(() => {
      if (recorder && recorder.state === 'recording') recorder.requestData();
    }, segmentLenRef.current * 1000);
    setRecPhase(REC.RECORDING);
    sendWs({ type: 'STATE_UPDATE', state: 'recording' });
    sendWs({ type: 'SESSION_RESUME' });
  }, [nowMs, startVisualizer, sendWs]);

  const handleRecord = useCallback(async () => {
    if (!title.trim()) { setError('A title is required.'); return; }
    setError('');
    setIsInitiating(true);
    mixpanel.track('createSessionClicked', { hasDescription: !!description.trim() });
    let data;
    try {
      data = await callInitiate();
    } catch (err) {
      setIsInitiating(false);
      const message = err.message || 'Could not start the session. Please try again.';
      setError(message);
      mixpanel.track('createSessionFailed', { step: 'initiate', errorMessage: message });
      return;
    }

    const newSessionId = data.sessionId || data.sessionStreamToken || '';
    sessionIdRef.current = newSessionId;
    sessionTitleRef.current = title.trim();
    setSessionId(newSessionId);
    setQrUrl(data.qrUrl || '');
    setJoinCode(data.joinCode || '');
    segmentLenRef.current = Number(data.segmentLength) > 0 ? Number(data.segmentLength) : SEGMENT_LENGTH_FALLBACK;

    let prep;
    try {
      prep = await callPrepareTutor();
    } catch (err) {
      setIsInitiating(false);
      const message = err.message || 'Could not initiate streaming. Please try again.';
      setError(message);
      mixpanel.track('createSessionFailed', { step: 'prepareTutor', sessionId: newSessionId, errorMessage: message });
      return;
    }
    credentialRef.current = prep.uploadCredential;
    segmentLenRef.current = Number(prep.segmentLength) > 0 ? Number(prep.segmentLength) : segmentLenRef.current;
    serverAnchorMsRef.current = (prep.serverTime || Math.floor(Date.now() / 1000)) * 1000;
    joinPerfRef.current = performance.now();
    segmentIndexRef.current = 0;
    try {
      sessionStorage.setItem(TUTOR_SESSION_KEY, JSON.stringify({
        ownerId: tutorId,
        sessionId: newSessionId,
        qrUrl: data.qrUrl || '',
        joinCode: data.joinCode || '',
        title: title.trim(),
        uploadCredential: prep.uploadCredential,
        segmentLength: segmentLenRef.current,
        segmentIndex: 0,
        storedAt: Date.now(),
      }));
    } catch { }

    closedRef.current = false;
    openSocket(newSessionId);

    sessionStartWallClockRef.current = Date.now();
    mixpanel.track('sessionCreated', { sessionId: newSessionId, hasDescription: !!description.trim() });
    mixpanel.people.increment('sessionsCreated');
    mixpanel.people.set_once({ firstSessionCreatedAt: new Date().toISOString() });

    setIsInitiating(false);
    setStep(STEP.CONTROL);
  }, [title, description, callInitiate, callPrepareTutor, openSocket]);

  const handleStopStreaming = useCallback(() => {
    if (isStoppingRef.current) return;
    isStoppingRef.current = true;
    mixpanel.track('stopStreamingClicked', { sessionId: sessionIdRef.current, elapsedSeconds: elapsedRef.current, deviceCount: rosterRef.current.length || 1 });

    if (timerRef.current) { clearInterval(timerRef.current); timerRef.current = null; }
    if (segmentTimerRef.current) { clearInterval(segmentTimerRef.current); segmentTimerRef.current = null; }
    freezeVisualizer();
    setRecPhase(REC.PAUSED);
    setStopping(true);

    setRoster((r) =>
      r.length
        ? r.map((d) => (d.deviceId === TUTOR_DEVICE_ID ? { ...d, state: DEVICE_STATE.STOPPED } : d))
        : [{ deviceId: TUTOR_DEVICE_ID, role: ROLE.TUTOR, state: DEVICE_STATE.STOPPED }],
    );

    const recorder = recorderRef.current;
    const finishAndAdvance = async () => {
      await Promise.allSettled(uploadsRef.current);
      sendWs({ type: 'STATE_UPDATE', state: 'stopped' });
      sendWs({ type: 'SESSION_STOP' });
      stopTimeoutRef.current = setTimeout(() => {
        handleAllStopped(true);
      }, 20000);
    };

    if (recorder && recorder.state !== 'inactive') {
      recorder.addEventListener('stop', () => { finishAndAdvance(); }, { once: true });
      recorder.stop();
    } else {
      finishAndAdvance();
    }
  }, [sendWs, handleAllStopped, freezeVisualizer]);

  const isRecording = recPhase === REC.RECORDING;
  const isPaused = recPhase === REC.PAUSED;
  const isRecLive = isRecording || isPaused;
  const anyDeviceRecording = isRecLive || roster.some((d) => d.state === DEVICE_STATE.RECORDING || d.state === DEVICE_STATE.PAUSED);

  useEffect(() => {
    const trulyActive = step === STEP.CONTROL;
    if (onSessionActiveChange) onSessionActiveChange(trulyActive);
    return () => {
      if (onSessionActiveChange) onSessionActiveChange(false);
    };
  }, [step, onSessionActiveChange]);

  useEffect(() => {
    rosterRef.current = roster;
    const auxCount = roster.filter((d) => d.role === ROLE.AUX).length;
    if (auxCount > prevAuxCountRef.current) {
      mixpanel.track('deviceJoined', {
        sessionId: sessionIdRef.current,
        auxCount,
        totalDeviceCount: roster.length,
      });
    }
    prevAuxCountRef.current = auxCount;
  }, [roster]);

  useEffect(() => {
    const handler = (e) => {
      if (step === STEP.CONTROL) {
        e.preventDefault();
        e.returnValue = '';
      }
    };
    window.addEventListener('beforeunload', handler);
    return () => {
      window.removeEventListener('beforeunload', handler);
    };
  }, [step]);

  useImperativeHandle(ref, () => ({
    stopSession: () => {
      if (isStoppingRef.current) return;
      isStoppingRef.current = true;
      if (segmentTimerRef.current) { clearInterval(segmentTimerRef.current); segmentTimerRef.current = null; }
      const wasActuallyRecording = anyDeviceRecording;
      const cleanupAndSend = async () => {
        await Promise.allSettled(uploadsRef.current);
        if (wasActuallyRecording) {
          sendWs({ type: 'STATE_UPDATE', state: 'stopped' });
          sendWs({ type: 'SESSION_STOP' });
        }
        try { sessionStorage.removeItem(TUTOR_SESSION_KEY); } catch { /* ignore */ }
        stopTimers();
        cleanupMedia();
        cleanupSocket();
      };
      const recorder = recorderRef.current;
      if (recorder && recorder.state !== 'inactive') {
        return new Promise((resolve) => {
          recorder.addEventListener('stop', () => { cleanupAndSend().finally(resolve); }, { once: true });
          recorder.stop();
        });
      } else {
        return cleanupAndSend();
      }
    },
  }), [anyDeviceRecording, sendWs, stopTimers, cleanupMedia, cleanupSocket]);

  useEffect(() => {
    const timer = setTimeout(() => {
      let stored = null;
      try {
        const raw = sessionStorage.getItem(TUTOR_SESSION_KEY);
        if (raw) stored = JSON.parse(raw);
      } catch { /* ignore */ }
      if (!stored || !stored.sessionId) return;
      if (stored.ownerId !== tutorId) {
        try { sessionStorage.removeItem(TUTOR_SESSION_KEY); } catch { /* ignore */ }
        return;
      }

      sessionIdRef.current = stored.sessionId;
      sessionTitleRef.current = stored.title || '';
      setSessionId(stored.sessionId);
      setQrUrl(stored.qrUrl || '');
      setJoinCode(stored.joinCode || '');
      setTitle(stored.title || '');
      credentialRef.current = stored.uploadCredential;
      segmentLenRef.current = Number(stored.segmentLength) > 0 ? Number(stored.segmentLength) : SEGMENT_LENGTH_FALLBACK;
      segmentIndexRef.current = stored.segmentIndex || 0;
      serverAnchorMsRef.current = Date.now();
      joinPerfRef.current = performance.now();

      mixpanel.track('sessionRestoredFromReload', { sessionId: stored.sessionId, segmentIndexAtRestore: stored.segmentIndex || 0 });

      closedRef.current = false;
      setStep(STEP.CONTROL);
      openSocket(stored.sessionId);
    }, 0);
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const outerSx = { padding: isMobile ? '16px' : '24px 28px', fontFamily: FONT_FAMILY };
  const contentSx = { maxWidth: 780, margin: '0 auto', padding: isMobile ? 0 : '0px 32px' };
  const primaryBtnSx = {
    py: isMobile ? 2 : 1.6, fontSize: isMobile ? '15px' : '14px', fontWeight: 600,
    textTransform: 'none', borderRadius: '4px',
    backgroundColor: COLORS.primaryDark, boxShadow: 'none',
    '&:hover': BUTTON_HOVER_SX,
    '&.Mui-disabled': { backgroundColor: COLORS.disabled, color: COLORS.page },
  };
  const regionSx = { border: `1px solid ${COLORS.hairline}`, borderRadius: '12px', p: 2.5, mb: 2 };
  const stopBtnSx = {
    width: 'fit-content',
    py: isMobile ? 1.4 : 1,
    px: 3,
    fontSize: isMobile ? '14px' : '13.5px',
    fontWeight: 600,
    textTransform: 'none',
    borderRadius: '4px',
    backgroundColor: RED,
    boxShadow: 'none',
    '&:hover': { backgroundColor: '#b91c1c', boxShadow: 'none' },
    '&.Mui-disabled': { backgroundColor: COLORS.disabled, color: COLORS.page },
  };

  const HelpLink = ({ label, onClick }) => (
    <Box
      onClick={onClick}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onClick(); } }}
      sx={{ display: 'flex', alignItems: 'center', gap: '6px', cursor: 'pointer' }}
    >
      <Typography sx={{ fontSize: '14px', color: COLORS.primary, fontWeight: 500 }}>
        {label}
      </Typography>
      <Box sx={{ width: 18, height: 18, borderRadius: '50%', backgroundColor: COLORS.primary, display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#fff', fontSize: '11px', fontWeight: 700, flexShrink: 0 }}>
        i
      </Box>
    </Box>
  );

  const Header = ({ label, helper, action }) => (
    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3, flexWrap: 'wrap', gap: 1 }}>
      <Typography sx={{ fontSize: '20px', fontWeight: 600, color: '#111827' }}>
        {label}
      </Typography>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
        {helper && (
          <Typography sx={{ fontSize: '14px', color: COLORS.textMuted }}>
            {helper}
          </Typography>
        )}
        {action}
      </Box>
    </Box>
  );

  if (step === STEP.SETUP) {
    return (
      <ThemeProvider theme={appTheme}>
        <Box sx={outerSx}>
          <StreamHelpDialog open={helpOpen} onClose={() => setHelpOpen(false)} section={helpSection} />

          <Header
            label="Prepare for Session Streaming"
            action={<HelpLink label="Help me prepare for streaming" onClick={() => openHelp('main')} />}
          />
          <Box sx={contentSx}>
            <Typography sx={{ fontSize: '15px', color: COLORS.textMuted, mb: 3, lineHeight: 1.6 }}>
              Give your session a Title and optional Description, then click or tap "Create Session" to prepare for streaming.
            </Typography>

            <TextField
              fullWidth variant="outlined" label="Session title*"
              value={title} onChange={(e) => { if (e.target.value.length <= 100) setTitle(e.target.value); }}
              placeholder="e.g. Math class - Week 3"
              helperText={`${title.length}/100 characters`} inputProps={{ maxLength: 100 }}
              sx={{ mb: 2, '& .MuiOutlinedInput-root': { borderRadius: '8px' } }}
            />
            <TextField
              fullWidth multiline rows={isMobile ? 3 : 4} variant="outlined" label="Description (optional)"
              value={description} onChange={(e) => { if (e.target.value.length <= 256) setDescription(e.target.value); }}
              placeholder="Topic, class period, or notes"
              helperText={`${description.length}/256 characters`} inputProps={{ maxLength: 256 }}
              sx={{ mb: 3, '& .MuiOutlinedInput-root': { borderRadius: '8px' } }}
            />

            {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

            <Button
              fullWidth variant="contained" onClick={handleRecord}
              disabled={!title.trim() || isInitiating}
              startIcon={isInitiating ? <CircularProgress size={18} color="inherit" /> : null}
              sx={primaryBtnSx}
            >
              {isInitiating ? 'Starting…' : 'Create Session'}
            </Button>
          </Box>
        </Box>
      </ThemeProvider>
    );
  }


  const auxCount = roster.filter((d) => d.role === ROLE.AUX).length;

  return (
    <ThemeProvider theme={appTheme}>
      <Box sx={outerSx}>
        <style>{`
          @keyframes tt-rec-blink { 0%,100% { opacity: 1; } 50% { opacity: 0.25; } }
        `}</style>

        <StreamHelpDialog open={helpOpen} onClose={() => setHelpOpen(false)} section={helpSection} />

        <Header
          label="Live Session"
          action={
            <HelpLink
              label={isRecLive ? 'Help me Manage and Finish a Session Stream' : 'Help me Start a Classroom Session Stream'}
              onClick={() => openHelp('main')}
            />
          }
        />

        <Box sx={contentSx}>
          {stopping && (
            <Alert
              severity="info"
              icon={<CircularProgress size={16} sx={{ color: BLUE }} />}
              sx={{ mb: 2, fontSize: '13.5px', fontWeight: 500, alignItems: 'center' }}
            >
              Stopping all devices and wrapping up the session…
            </Alert>
          )}

          <Box sx={{ ...regionSx, textAlign: 'center' }}>
            {!isRecLive && (
              <>
                {recError && <Alert severity="error" sx={{ mb: 2, textAlign: 'left' }}>{recError}</Alert>}
                <Typography sx={{ fontSize: '16px', fontWeight: 600, color: '#111827', mb: 0.5 }}>
                  {title.trim() || 'Untitled session'}
                </Typography>
                <Typography sx={{ fontSize: '14px', color: COLORS.textMuted, mb: 2 }}>
                  Record from this device
                </Typography>
                <Button
                  variant="contained" onClick={startRecording}
                  disabled={recPhase === REC.REQUESTING}
                  startIcon={recPhase === REC.REQUESTING ? <CircularProgress size={16} color="inherit" /> : <MicNoneIcon />}
                  sx={{ ...primaryBtnSx, px: 4 }}
                >
                  {recPhase === REC.REQUESTING ? 'Starting…' : 'Start Streaming'}
                </Button>

                {anyDeviceRecording && (
                  <Box sx={{ mt: 2, display: 'flex', justifyContent: 'center' }}>
                    <Button
                      variant="contained"
                      onClick={handleStopStreaming}
                      disabled={stopping}
                      startIcon={stopping ? <CircularProgress size={18} color="inherit" /> : <StopRoundedIcon />}
                      sx={stopBtnSx}
                    >
                      {stopping ? 'Stopping all devices…' : 'Stop Streaming & Generate Talk Tree'}
                    </Button>
                  </Box>
                )}
              </>
            )}

            {isRecLive && (
              <>
                <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.75, borderRadius: '999px', px: 1.5, py: 0.5, fontSize: '13px', fontWeight: 600, backgroundColor: isRecording ? '#fef2f2' : '#f3f4f6', color: isRecording ? RED : COLORS.textMuted }}>
                  <Box sx={{ width: 9, height: 9, borderRadius: '50%', backgroundColor: isRecording ? RED : '#9ca3af', animation: isRecording ? 'tt-rec-blink 1.2s ease-in-out infinite' : 'none' }} />
                  {isRecording ? 'Recording (this device)' : 'Paused (this device)'}
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'flex-end', justifyContent: 'center', gap: '5px', height: 48, mt: 2, mb: 2.5 }}>
                  {bars.map((b, i) => (
                    <Box key={i} sx={{ width: 8, borderRadius: '5px', height: `${Math.max(6, b * 48)}px`, backgroundColor: isRecording ? BLUE : '#d1d5db', transition: 'height 0.08s ease-out' }} />
                  ))}
                </Box>

                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, alignItems: 'center' }}>
                  <Button
                    variant="outlined" onClick={isRecording ? pauseAll : resumeAll}
                    disabled={stopping}
                    startIcon={isRecording ? <PauseRoundedIcon /> : <PlayArrowRoundedIcon />}
                    sx={{
                      py: isMobile ? 1.4 : 1, px: 3, fontSize: isMobile ? '14px' : '13.5px', fontWeight: 600, textTransform: 'none',
                      borderRadius: '4px', borderColor: '#d1d5db', color: COLORS.text,
                      '&:hover': { borderColor: '#9ca3af', backgroundColor: '#f9fafb' },
                    }}
                  >
                    {isRecording ? 'Pause All' : 'Resume All'}
                  </Button>

                  <Button
                    variant="contained"
                    onClick={handleStopStreaming}
                    disabled={stopping || !anyDeviceRecording}
                    startIcon={stopping ? <CircularProgress size={18} color="inherit" /> : <StopRoundedIcon />}
                    sx={stopBtnSx}
                  >
                    {stopping ? 'Stopping…' : 'Stop Streaming & Generate Talk Tree'}
                  </Button>
                </Box>
              </>
            )}
          </Box>

          {!wsConnected && (
            <Alert severity="warning" sx={{ mb: 2, fontSize: '13px' }}>
              Connection lost — reconnecting… Recording continues on this device either way.
            </Alert>
          )}

          <Box sx={{ border: `1px solid ${COLORS.hairline}`, borderRadius: '12px', p: 2, mb: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 1 }}>
              <Button
                onClick={() => {
                  const next = !showQr;
                  setShowQr(next);
                  mixpanel.track('addDevicePanelToggled', { sessionId: sessionIdRef.current, opened: next });
                }}
                startIcon={<PersonAddAltOutlinedIcon />}
                sx={{ justifyContent: 'flex-start', textTransform: 'none', fontSize: '15px', fontWeight: 600, color: '#111827', '&:hover': { backgroundColor: '#f9fafb' } }}
              >
                {showQr ? 'Hide QR code' : 'Add a device'}
              </Button>
              {showQr && (
                <HelpLink label="Help me stream from additional devices" onClick={() => openHelp('devices')} />
              )}
            </Box>
            <Collapse in={showQr}>
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: isMobile ? 'column' : 'row',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: isMobile ? 2.5 : 4,
                  pt: 2.5,
                  pb: 1,
                }}
              >
                {/* Option 1: scan */}
                <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1.5 }}>
                  <Typography sx={{ fontSize: '14px', fontWeight: 600, color: '#111827' }}>
                    Scan with the device's camera
                  </Typography>
                  <Box sx={{ border: `1px solid ${COLORS.hairline}`, borderRadius: '12px', p: 2, backgroundColor: COLORS.white }}>
                    {qrUrl
                      ? <QRCodeSVG value={buildQrTarget(qrUrl, sessionId)} size={isMobile ? 160 : 170} level="M" />
                      : <CircularProgress size={24} sx={{ color: BLUE }} />}
                  </Box>
                </Box>

                {joinCode && (
                  <>
                    {/* Divider: vertical on desktop, horizontal on mobile */}
                    <Box sx={{ display: 'flex', flexDirection: isMobile ? 'row' : 'column', alignItems: 'center', gap: 1, alignSelf: 'stretch' }}>
                      <Box sx={{ flex: 1, [isMobile ? 'height' : 'width']: '1px', backgroundColor: COLORS.hairline }} />
                      <Typography sx={{ fontSize: '12px', color: COLORS.textMuted, fontWeight: 600 }}>or</Typography>
                      <Box sx={{ flex: 1, [isMobile ? 'height' : 'width']: '1px', backgroundColor: COLORS.hairline }} />
                    </Box>

                    {/* Option 2: type a code */}
                    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1.5, maxWidth: 260 }}>
                      <Typography sx={{ fontSize: '14px', fontWeight: 600, color: '#111827' }}>
                        No camera? Enter this code
                      </Typography>
                      <Box
                        sx={{
                          border: `1px solid ${COLORS.hairline}`, borderRadius: '12px',
                          px: 3, py: 1.5, backgroundColor: COLORS.white,
                        }}
                      >
                        <Typography
                          sx={{
                            fontSize: isMobile ? '32px' : '36px', fontWeight: 700, lineHeight: 1.1,
                            letterSpacing: '0.18em', mr: '-0.18em', color: '#111827',
                            fontVariantNumeric: 'tabular-nums', userSelect: 'all',
                          }}
                        >
                          {joinCode.toUpperCase()}
                        </Typography>
                      </Box>
                      <Button
                        size="small"
                        onClick={() => { navigator.clipboard?.writeText(joinCode.toUpperCase()); }}
                        sx={{ textTransform: 'none', fontSize: '13px', fontWeight: 600, color: BLUE }}
                      >
                        Copy code
                      </Button>
                      <Typography sx={{ fontSize: '13px', color: COLORS.textMuted, textAlign: 'center', lineHeight: 1.5 }}>
                        On the other device, open <a href="https://app.talktrees.ai/join" target="_blank" rel="noopener noreferrer" style={{ color: BLUE, textDecoration: 'underline' }}>app.talktrees.ai/join</a> and type the code.
                      </Typography>
                    </Box>
                  </>
                )}
              </Box>

              <Typography sx={{ fontSize: '14px', color: COLORS.textMuted, mt: 2, textAlign: 'center' }}>
                Have another device scan this to join - no login needed. The same code works for every device.
              </Typography>
            </Collapse>
          </Box>

          {(auxCount > 0 || (stopping && roster.length > 0)) && (
            <Box sx={{ border: `1px solid ${COLORS.hairline}`, borderRadius: '12px', p: 2, mb: 3 }}>
              <Typography sx={{ fontSize: '13px', fontWeight: 700, color: COLORS.text, mb: 1 }}>
                Connected devices
              </Typography>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 600, fontSize: '12px', color: COLORS.textMuted, borderBottom: `1px solid ${COLORS.hairline}` }}>Device</TableCell>
                    <TableCell sx={{ fontWeight: 600, fontSize: '12px', color: COLORS.textMuted, borderBottom: `1px solid ${COLORS.hairline}` }}>Status</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {roster.map((d) => (
                    <TableRow key={d.deviceId}>
                      <TableCell sx={{ fontSize: '13px', color: '#111827', borderBottom: '1px solid #f9fafb' }}>
                        {d.deviceId}{d.role === ROLE.TUTOR ? ' (This Device)' : ''}
                      </TableCell>
                      <TableCell sx={{ borderBottom: '1px solid #f9fafb' }}>
                        <Chip label={d.state} sx={stateChipSx(d.state)} />
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </Box>
          )}
        </Box>
      </Box>
    </ThemeProvider>
  );
});

StreamSetup.displayName = 'StreamSetup';

export default StreamSetup;